import { PriceGuide } from '@models/guides/priceGuide.model';
import { EnvToken } from '@app/core/env.tokens';
import { AIPriceGuide, AIPriceGuidesByCatResponse } from '@models/guides/aiPriceGuide.model';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { urlDecode } from '@utils/urlHelpers';

import { PriceGuideOverview } from '@app/pages/guides/priceGuides/priceGuidesOverview/interface/priceGudeOverview.type.interface';
import {
  RelatedGuides,
  AutocompleteResponse,
  PriceGuideOverviewResponse,
} from '@app/pages/guides/aiPriceGuidesOverview/interface/aiPriceGuideOverview.type.interface';
import { PriceGuideOverview as AIPriceGuideOverviewCategory } from '@app/pages/guides/aiPriceGuidesOverview/interface/aiPriceGuideOverview.type.interface';

@Injectable({
  providedIn: 'root',
})
export class PriceGuideService {
  protected readonly env = inject(EnvToken);

  baseHref = `${this.env.dashboardBaseRef}api/${this.env.apiPrefix}price-guides`;

  constructor(private http: HttpClient) {}

  fetchPriceGuideBySlug(
    slug: string,
    mainCat: string,
    subCat?: string,
  ): Observable<PriceGuide> {
    const url = `${this.baseHref}/${slug!}/active`;
    let params = new HttpParams();
    if (mainCat) {
      const decodedMainCat = urlDecode(mainCat.toLowerCase().trim());
      params = params.append('mainCat', decodedMainCat);
    }
    if (subCat) {
      const decodedSubcat = urlDecode(subCat?.toLowerCase().trim());
      params = params.append('subCat', decodedSubcat);
    }
    return this.http.get<PriceGuide>(url, { params }).pipe(
      catchError((error) => {
        // If it's a redirect error (handled by interceptor), return empty
        // The interceptor will handle the actual redirect
        if (error.isRedirect) {
          return EMPTY;
        }
        throw error;
      })
    );
  }

  fetchAIPriceGuideBySlug(
    slug: string,
    mainCat: string,
    subCat?: string,
  ): Observable<AIPriceGuide> {
    const url = `${
      this.env.dashboardBaseRef
    }api/${this.env.apiPrefix}ai-price-guide/get-ai-price-guide/${slug!}`;
    let params = new HttpParams();
    if (mainCat) {
      const decodedMainCat = urlDecode(mainCat.toLowerCase().trim());
      params = params.append('mainCat', decodedMainCat);
    }
    if (subCat) {
      const decodedSubcat = urlDecode(subCat?.toLowerCase().trim());
      params = params.append('subCat', decodedSubcat);
    }
    return this.http.get<AIPriceGuide>(url, { params }).pipe(
      catchError((error) => {
        // If it's a redirect error (handled by interceptor), return empty
        // The interceptor will handle the actual redirect
        if (error.isRedirect) {
          return EMPTY;
        }
        throw error;
      })
    );
  }

  fetchActivePriceGuidesByCat(
    asCat: number,
    currentId: string,
  ): Observable<PriceGuide[]> {
    const url = `${this.baseHref}/active/${asCat}/${currentId}`;
    return this.http.get<PriceGuide[]>(url);
  }

  fetchPriceGuideCategories(): Observable<any[]> {
    const url = `${this.baseHref}/categories/active`;
    return this.http.get<any[]>(url);
  }

  fetchPriceGuidesOverview(): Observable<PriceGuideOverview[]> {
    const url = `${this.baseHref}/overview`;
    return this.http.get<PriceGuideOverview[]>(url);
  }

  fetchAIPriceGuidesOverview(
    page: number = 1,
    limit: number = 1000,
  ): Observable<PriceGuideOverviewResponse> {
    const url = `${this.env.dashboardBaseRef}api/${this.env.apiPrefix}ai-price-guide/get-ai-price-guide-overview`;
    const params = new HttpParams()
      .append('page', page.toString())
      .append('limit', limit.toString());

    return this.http
      .get<
        PriceGuideOverviewResponse | AIPriceGuideOverviewCategory[]
      >(url, { params })
      .pipe(
        map((response) =>
          // Backend without pagination support returns the bare array
          Array.isArray(response)
            ? {
                data: response,
                total: response.reduce(
                  (sum, cat) => sum + (cat.priceGuides?.length ?? 0),
                  0,
                ),
                page: 1,
                limit,
                totalPages: 1,
                hasMore: false,
              }
            : response,
        ),
      );
  }

  fetchRelatedAIPriceGuides(asCat: number): Observable<RelatedGuides[]> {
    const url = `${this.env.dashboardBaseRef}api/${this.env.apiPrefix}ai-price-guide/related?asCat=${asCat}`;
    return this.http.get<RelatedGuides[]>(url);
  }

  fetchAIPriceGuidesByCat(activeCategory: string): Observable<AIPriceGuidesByCatResponse> {
    const url = `${this.env.dashboardBaseRef}api/${this.env.apiPrefix}ai-price-guide/by-active-cat/${activeCategory}`;
    return this.http.get<AIPriceGuidesByCatResponse>(url);
  }

  searchAutocomplete(
    searchQuery: string,
    limit: number = 10,
    page: number = 1,
  ): Observable<AutocompleteResponse> {
    const url = `${this.env.dashboardBaseRef}api/${this.env.apiPrefix}ai-price-guide/auto-complete`;
    let params = new HttpParams()
      .append('searchQuery', searchQuery)
      .append('limit', limit.toString())
      .append('page', page.toString());

    return this.http.get<AutocompleteResponse>(url, { params });
  }
}
